import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface ConsentRetryCountdownProps {
  /** How long to wait before the retry is triggered, in milliseconds. */
  delayMs: number;
  /**
   * Triggered once the countdown runs out, or immediately when the admin skips
   * the wait. The implementation is expected to navigate the browser to Azure
   * AD, so it must never be invoked more than once.
   */
  onElapsed: () => void;
}

const TICK_MS = 1000;

/**
 * Waiting screen shown after Azure AD aborted a consent redirect with a
 * transient error. Re-running the step straight away tends to fail the same
 * way, so we pause first — visibly, with a countdown, so the admin can tell the
 * flow is still alive and isn't left wondering what happened.
 *
 * Purely presentational: it owns nothing but its timer and reports back through
 * `onElapsed`. Retry bookkeeping and the redirect itself live in the caller.
 */
export const ConsentRetryCountdown = ({ delayMs, onElapsed }: ConsentRetryCountdownProps) => {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState(() => Math.max(Math.ceil(delayMs / TICK_MS), 1));
  // A redirect must not be triggered twice — neither by the skip button racing
  // the last tick, nor by React re-running the effect (StrictMode does so in
  // development).
  const firedRef = useRef(false);
  const onElapsedRef = useRef(onElapsed);
  onElapsedRef.current = onElapsed;

  const fire = useCallback(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    onElapsedRef.current();
  }, []);

  useEffect(() => {
    if (remaining <= 0) {
      fire();
      return;
    }
    const timer = window.setTimeout(() => setRemaining((seconds) => seconds - 1), TICK_MS);
    return () => window.clearTimeout(timer);
  }, [remaining, fire]);

  return (
    <Stack spacing={2.5} alignItems="center">
      <Typography variant="h5" gutterBottom>
        {t('signup:consentStep:registerCallbackPage:transient_retry_title')}
      </Typography>
      <Typography color="text.secondary">
        {t('signup:consentStep:registerCallbackPage:transient_retry_description')}
      </Typography>
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        {t('signup:consentStep:registerCallbackPage:transient_retry_countdown', {
          count: remaining,
        })}
      </Typography>
      <Button
        variant="contained"
        color="primary"
        size="large"
        onClick={fire}
        sx={{ mt: 1, minWidth: 240 }}
      >
        {t('signup:consentStep:registerCallbackPage:transient_retry_now')}
      </Button>
    </Stack>
  );
};
